import React, { useEffect, useState } from 'react';
import { X, ExternalLink, BookOpen, Search, Share2 } from "lucide-react";
import api from "../../api/axios";
import { motion, AnimatePresence } from "framer-motion";

export default function ResourceModal({ isOpen, onClose, onSelectResource }) {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (isOpen) {
            fetchArticles();
        }
    }, [isOpen]);

    const fetchArticles = async () => {
        setLoading(true);
        try {
            // Fetch all public articles (or provider's own)
            const res = await api.get("/articles/explore");
            setArticles(res.data || []);
        } catch (err) {
            console.error("Failed to load articles", err);
        } finally {
            setLoading(false);
        }
    };

    const filtered = articles.filter(a =>
        a.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 10 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 10 }}
                        className="relative bg-white dark:bg-gray-900 w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden flex flex-col max-h-[85vh]"
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                            <div>
                                <h3 className="font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2 text-lg">
                                    <BookOpen className="w-5 h-5 text-brand-blue" />
                                    Library
                                </h3>
                                <p className="text-xs text-gray-500">Share knowledge with your client</p>
                            </div>
                            <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-500">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Search */}
                        <div className="p-4 border-b border-gray-100 dark:border-gray-800">
                            <div className="relative">
                                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search for guides, articles, or tools..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-brand-blue/20 outline-none text-sm transition-all"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* List */}
                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-gray-50/30 dark:bg-gray-900">
                            {loading ? (
                                <div className="py-20 text-center text-gray-400 text-sm animate-pulse">Syncing library...</div>
                            ) : filtered.length === 0 ? (
                                <div className="py-20 text-center">
                                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl">📚</div>
                                    <p className="text-gray-500 dark:text-gray-400 text-sm">No articles match your search.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {filtered.map(article => (
                                        <button
                                            key={article.id}
                                            onClick={() => onSelectResource(article)}
                                            className="group relative flex flex-col text-left bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl overflow-hidden hover:shadow-lg hover:border-brand-blue/30 dark:hover:border-brand-blue/30 transition-all duration-300"
                                        >
                                            <div className="h-32 w-full overflow-hidden bg-gray-200 relative">
                                                {article.coverImage ? (
                                                    <img src={article.coverImage} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-brand-blue/5 text-brand-blue font-bold text-sm">
                                                        Antaryatra
                                                    </div>
                                                )}
                                                {/* Overlay CTA */}
                                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-[1px]">
                                                    <span className="bg-white text-gray-900 px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-2 transform translate-y-2 group-hover:translate-y-0 transition-transform">
                                                        <Share2 size={12} /> Share
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="p-3">
                                                <h4 className="font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 text-sm leading-snug mb-1 group-hover:text-brand-blue transition-colors">
                                                    {article.title}
                                                </h4>
                                                <div className="flex items-center justify-between mt-2">
                                                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Article</span>
                                                    <span className="text-[10px] text-gray-400">By {article.author?.name || "Guide"}</span>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
